import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { clearSentryUser, setSentryUser } from '../lib/sentry';
import { identifyPostHogUser, resetPostHog } from '../lib/posthog';

export interface Profile {
  id: string;
  credits: number;
  is_active: boolean;
  role: 'user' | 'pro' | 'superadmin';
  max_image: number;
  created_at: string;
  updated_at: string;
  [key: string]: any;
}

interface UserState {
  // Auth state
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;

  // Computed selectors (derived state)
  isAuthenticated: boolean;
  isActive: boolean;
  isPro: boolean;
  isSuperAdmin: boolean;
  credits: number;

  // Actions - Auth
  setUser: (user: User | null) => void;
  setSession: (session: Session | null) => void;
  setProfile: (profile: Profile | null) => void;
  setLoading: (loading: boolean) => void;

  // Actions - Profile updates
  updateCredits: (delta: number) => void; // For optimistic updates
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;

  // Selectors - Permission checks
  canGenerate: () => boolean;
  canAccessTool: (toolId: string) => boolean;
  hasRole: (roles: string[]) => boolean;
}

export const useUserStore = create<UserState>()(
  immer((set, get) => ({
    // Initial state
    user: null,
    session: null,
    profile: null,
    loading: true,

    // Computed selectors
    get isAuthenticated() {
      return !!get().user;
    },
    get isActive() {
      const profile = get().profile;
      return !!profile?.is_active || profile?.role === 'superadmin';
    },
    get isPro() {
      const profile = get().profile;
      return profile?.role === 'pro' || profile?.role === 'superadmin';
    },
    get isSuperAdmin() {
      return get().profile?.role === 'superadmin';
    },
    get credits() {
      return get().profile?.credits ?? 0;
    },

    // Actions - Auth
    setUser: (user) => {
      set((state) => {
        state.user = user;
      });
    },

    setSession: (session) => {
      set((state) => {
        state.session = session;
      });
    },

    setProfile: (profile) => {
      set((state) => {
        state.profile = profile;

        // Update monitoring services when profile changes
        if (profile) {
          setSentryUser(profile.id, profile.email, profile.full_name);
          identifyPostHogUser(profile.id, {
            email: profile.email,
            role: profile.role,
            credits: profile.credits,
            is_active: profile.is_active,
          });
        } else {
          clearSentryUser();
          resetPostHog();
        }
      });
    },

    setLoading: (loading) => {
      set((state) => {
        state.loading = loading;
      });
    },

    // Actions - Profile updates
    updateCredits: (delta) => {
      set((state) => {
        if (state.profile) {
          state.profile.credits += delta;
        }
      });
    },

    refreshProfile: async () => {
      const userId = get().user?.id;
      if (!userId) return;

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (error) throw error;
        get().setProfile(data);
      } catch (error) {
        console.error('Failed to refresh profile:', error);
      }
    },

    signOut: async () => {
      // Clear monitoring
      clearSentryUser();
      resetPostHog();

      // Clear Supabase session
      await supabase.auth.signOut();

      // Clear local state
      set({
        user: null,
        session: null,
        profile: null,
        loading: false,
      });
    },

    // Selectors - Permission checks
    canGenerate: () => {
      const state = get();
      return state.isActive && state.credits > 0;
    },

    canAccessTool: (toolId: string) => {
      const state = get();
      
      // Superadmin can access everything
      if (state.isSuperAdmin) return true;
      
      // Inactive users can't access tools
      if (!state.isActive) return false;

      // Tool-specific restrictions can be added here
      // For now, all active users can access all tools
      return true;
    },

    hasRole: (roles: string[]) => {
      const profile = get().profile;
      if (!profile) return false;
      return roles.includes(profile.role);
    },
  }))
);

/**
 * Initialize user store by syncing with Supabase auth
 * Call this once in your app entry point
 */
export function initializeUserStore() {
  const store = useUserStore.getState();

  // Get initial session
  supabase.auth.getSession().then(({ data: { session } }) => {
    store.setSession(session);
    store.setUser(session?.user ?? null);
    if (session?.user) {
      fetchProfile(session.user.id);
    } else {
      store.setLoading(false);
    }
  });

  // Listen for auth changes
  supabase.auth.onAuthStateChange((event, session) => {
    store.setSession(session);
    store.setUser(session?.user ?? null);

    if (session?.user) {
      fetchProfile(session.user.id);
    } else {
      store.setProfile(null);
      store.setLoading(false);
    }
  });

  // Subscribe to profile changes via Supabase Realtime
  let realtimeChannel: any = null;

  const setupRealtime = (userId: string) => {
    // Cleanup existing channel
    if (realtimeChannel) {
      supabase.removeChannel(realtimeChannel);
    }

    realtimeChannel = supabase
      .channel(`profile:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${userId}`,
        },
        (payload) => {
          const store = useUserStore.getState();
          store.setProfile(payload.new as Profile);
        }
      )
      .subscribe();
  };

  // Cleanup on unmount
  return () => {
    if (realtimeChannel) {
      supabase.removeChannel(realtimeChannel);
    }
  };
}

async function fetchProfile(userId: string) {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) throw error;

    const store = useUserStore.getState();
    store.setProfile(data);
  } catch (error) {
    console.error('Error fetching profile:', error);
  } finally {
    const store = useUserStore.getState();
    store.setLoading(false);
  }
}

/**
 * Hook to check if user can perform generation
 */
export function useCanGenerate() {
  const canGenerate = useUserStore((state) => state.canGenerate());
  const credits = useUserStore((state) => state.credits);
  const isActive = useUserStore((state) => state.isActive);

  return {
    canGenerate,
    credits,
    isActive,
    reason: !isActive ? 'inactive' : credits <= 0 ? 'no_credits' : null,
  };
}

/**
 * Hook to check user roles
 */
export function useHasRole(roles: string[]) {
  return useUserStore((state) => state.hasRole(roles));
}
